import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { DisplayInput } from "@/components/Atoms/DisplayField/DisplayInput";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import type { ActivityListItem } from "@/components/Molecules/ActivityList/ActivityList";
import { ActivityList } from "@/components/Molecules/ActivityList/ActivityList";
import { EmptyState } from "@/components/Molecules/EmptyState/EmptyState";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import type { AuditLogDisplay } from "@/services/job/job.types";
import { organizationQueryOptions } from "@/services/organization";
import { userQueryOptions } from "@/services/user";
import { formatLongDate } from "@/utils/utils";
import { SearchSchema } from "../../$orgUserId";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/activity",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/activity",
	});
	const userId = params.userId;
	const entityLabel = t("user.entityLabel");
	const userById = useQuery(
		organizationQueryOptions.users.detail(params.orgId, params.orgUserId),
	);
	const pageSize = 10;
	const historyCountQuery = useQuery(userQueryOptions.history.count(userId));
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			userQueryOptions.history.infinite(
				userId,
				pageSize,
				historyCountQuery.data,
			),
		);

	const logs = useMemo(() => {
		if (!data?.pages) return [];
		return [...(data.pages.flat() as AuditLogDisplay[])].sort((a, b) => {
			if (!a.date) return 1;
			if (!b.date) return -1;
			return new Date(b.date).getTime() - new Date(a.date).getTime();
		});
	}, [data]);
	const actors = useMemo(() => {
		const byId = new Map<string, boolean>();
		for (const log of logs) {
			const actorId = log.user?.id ?? log.userId;
			if (!actorId) continue;
			const actorHasAvatar = log.user?.hasAvatar === true;
			if (byId.get(actorId)) continue;
			if (actorHasAvatar) byId.set(actorId, true);
			else if (!byId.has(actorId)) byId.set(actorId, false);
		}
		return [...byId];
	}, [logs]);
	const [avatarSrcByUserId, setAvatarSrcByUserId] = useState<
		Record<string, string | null>
	>({});
	const setActorAvatarSrc = useCallback(
		(actorId: string, src: string | null) => {
			setAvatarSrcByUserId((current) => {
				if (current[actorId] === src) return current;
				return { ...current, [actorId]: src };
			});
		},
		[],
	);

	const items: ActivityListItem[] = useMemo(() => {
		return logs.map((log, index) => {
			const displayName = getUserDisplayName(log.user);
			const dateLabel = log.date ? formatLongDate(log.date) : "";
			const actorId = log.user?.id ?? log.userId ?? undefined;
			const avatarSrc = actorId ? avatarSrcByUserId[actorId] : null;
			return {
				id: log.id,
				primaryText: getEventTitle(log, t, entityLabel),
				secondaryText: [displayName, dateLabel].filter(Boolean).join(" • "),
				avatarAlt: displayName,
				avatarSrc: avatarSrc || undefined,
				avatarInitials: avatarSrc ? undefined : getUserInitials(log.user),
				defaultExpanded: index === 0,
				content: (
					<ActivityEventContent
						log={log}
						entityLabel={entityLabel}
						fromLabel={t("main.history.from")}
						toLabel={t("main.history.to")}
					/>
				),
			};
		});
	}, [avatarSrcByUserId, entityLabel, logs, t]);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "0px 0px 300px 0px",
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	const history = isLoading ? (
		<div className="flex w-full min-h-[280px] flex-col items-center justify-center py-8">
			<LoadingIndicator type="line-simple" size="lg" supportingText />
		</div>
	) : items.length === 0 ? (
		<EmptyState
			type="default"
			title={t("org-admin.users.details.activity.history.empty")}
			description={t("org-admin.users.details.activity.history.description")}
			showAction={false}
			className="min-h-[280px] max-w-none py-8"
		/>
	) : (
		<div className="flex w-full flex-col gap-2">
			{actors.map(([actorId, actorHasAvatar]) => (
				<ActivityActorAvatar
					key={actorId}
					userId={actorId}
					hasAvatar={actorHasAvatar}
					onSrc={setActorAvatarSrc}
				/>
			))}
			<ActivityList
				items={items}
				aria-label={t("org-admin.users.tabs.activity")}
			/>
			{(hasNextPage || isFetchingNextPage) && (
				<div
					ref={loadMoreRef}
					className="flex items-center justify-center py-3"
				>
					{isFetchingNextPage ? (
						<LoadingIndicator type="line-simple" size="sm" />
					) : null}
				</div>
			)}
		</div>
	);

	return (
		<div className="flex w-full flex-col">
			<DisplayInput
				label={t("org-admin.users.details.activity.activated")}
				value={formatActivityDate(userById.data?.activated)}
				showEdit={false}
			/>
			<DisplayInput
				label={t("org-admin.users.details.activity.lastUpdate")}
				value={formatActivityDate(userById.data?.lastUpdated)}
				showEdit={false}
			/>
			<DisplayInput
				label={t("org-admin.users.details.activity.lastLogin")}
				value={formatActivityDate(userById.data?.lastLoggedIn, true)}
				showEdit={false}
			/>
			<DisplayInput
				label={t("org-admin.users.details.activity.lastStatusChange")}
				value={formatActivityDate(userById.data?.lastStatusUpdated)}
				showEdit={false}
			/>
			{history}
		</div>
	);
}

function ActivityActorAvatar({
	userId,
	hasAvatar,
	onSrc,
}: {
	userId: string;
	hasAvatar: boolean;
	onSrc: (userId: string, src: string | null) => void;
}) {
	const { src } = useUserAvatarSrc(userId, hasAvatar);
	useEffect(() => {
		onSrc(userId, src);
		return () => onSrc(userId, null);
	}, [onSrc, src, userId]);
	return null;
}

function formatActivityDate(
	value: string | null | undefined,
	includeTime = false,
) {
	if (!value) return undefined;
	return formatLongDate(value, includeTime ? { includeTime: true } : undefined);
}

function ActivityEventContent({
	log,
	entityLabel,
	fromLabel,
	toLabel,
}: {
	log: AuditLogDisplay;
	entityLabel: string;
	fromLabel: string;
	toLabel: string;
}) {
	const { t } = useAppTranslation();
	const hasOld = log.oldValue != null && log.oldValue !== "";
	const hasNew = log.newValue != null && log.newValue !== "";
	const action = (log.action ?? "").toLowerCase();

	if (!hasOld && !hasNew) {
		if (action === "create") {
			return (
				<p className="text-sm text-secondary">
					{t("common.activity.entityWasCreated", { entity: entityLabel })}
				</p>
			);
		}
		if (action === "delete") {
			return (
				<p className="text-sm text-secondary">
					{t("common.activity.entityWasDeleted", { entity: entityLabel })}
				</p>
			);
		}
		return (
			<p className="text-sm text-secondary">
				{getEventTitle(log, t, entityLabel)}
			</p>
		);
	}

	const fieldLabel = formatFieldName(log.fieldName);
	return (
		<div className="flex flex-col gap-2 text-sm">
			{log.fieldName ? (
				<p className="font-semibold text-primary capitalize">{fieldLabel}</p>
			) : null}
			<div className="flex flex-wrap items-center gap-1.5 text-secondary">
				{hasOld ? (
					<>
						<span className="text-tertiary">{fromLabel}</span>
						<span>"{log.oldValue}"</span>
					</>
				) : null}
				{hasNew ? (
					<>
						<span className="text-tertiary">{toLabel}</span>
						<span>"{log.newValue}"</span>
					</>
				) : null}
			</div>
		</div>
	);
}

function getEventTitle(
	log: AuditLogDisplay,
	t: (key: string, options?: Record<string, string>) => string,
	entityLabel: string,
): string {
	const action = (log.action ?? "").toLowerCase();
	const field = formatFieldName(log.fieldName);
	if (action === "create") {
		return t("common.activity.createdEntity", { entity: entityLabel });
	}
	if (action === "delete") {
		return t("common.activity.deletedEntity", { entity: entityLabel });
	}
	if (action === "update") {
		if (log.fieldName) {
			return t("common.activity.updatedField", { field: capitalize(field) });
		}
		return t("common.activity.updatedEntity", { entity: entityLabel });
	}
	if (log.action) return capitalize(log.action);
	return t("common.activity.event");
}

function getUserDisplayName(user: AuditLogDisplay["user"]): string {
	if (!user) return "";
	if (user.firstName && user.lastName) {
		return `${user.firstName} ${user.lastName}`;
	}
	if (user.displayName) return user.displayName;
	if (user.email) return user.email;
	return "";
}

function getUserInitials(user: AuditLogDisplay["user"]): string {
	if (!user) return "?";
	const firstName = user.firstName || "";
	const lastName = user.lastName || "";
	if (firstName && lastName) {
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	}
	if (user.displayName) {
		const parts = user.displayName.trim().split(/\s+/);
		if (parts.length >= 2) {
			return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
		}
		return user.displayName.substring(0, 2).toUpperCase();
	}
	if (user.email) return user.email.substring(0, 2).toUpperCase();
	return "?";
}

function formatFieldName(fieldName: string | null | undefined): string {
	if (!fieldName) return "";
	return fieldName
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
		.toLowerCase();
}

function capitalize(value: string): string {
	if (!value) return value;
	return value.charAt(0).toUpperCase() + value.slice(1);
}
